
import { defineComponent, nextTick, PropType } from "vue";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { deleteContentMedia, listContentMedia } from "@/api/ghlContent.api.js";

const GRID_MIN_COLUMN_PX = 140;
const GRID_GAP_PX = 12;

export default defineComponent({
  name: "content-media-library",
  props: {
    selectedIds: {
      type: Array as PropType<number[]>,
      default: () => []
    },
    imagesOnly: {
      type: Boolean,
      default: false
    },
    maxSelection: {
      type: Number,
      default: 0
    },
    singleVideo: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    maxRows: {
      type: Number,
      default: 3
    }
  },
  emits: ["update:selectedIds", "refresh"],
  data() {
    return {
      loading: true,
      files: [] as any[],
      usedBytes: 0,
      limitBytes: 0,
      page: 1,
      gridWidth: 0,
      resizeObserver: null as { disconnect: () => void; observe: (el: Element) => void } | null
    };
  },
  computed: {
    storagePercent(): number {
      if (!this.limitBytes) return 0;
      return Math.min(100, Math.round((this.usedBytes / this.limitBytes) * 100));
    },
    visibleFiles(): any[] {
      if (!this.imagesOnly) {
        return this.files;
      }
      return this.files.filter((file) => this.isImage(file));
    },
    gridColumns(): number {
      if (!this.gridWidth) return 1;
      return Math.max(
        1,
        Math.floor((this.gridWidth + GRID_GAP_PX) / (GRID_MIN_COLUMN_PX + GRID_GAP_PX))
      );
    },
    pageSize(): number {
      return Math.max(1, this.gridColumns * this.maxRows);
    },
    totalPages(): number {
      if (!this.visibleFiles.length) return 1;
      return Math.ceil(this.visibleFiles.length / this.pageSize);
    },
    paginatedFiles(): any[] {
      const safePage = Math.min(this.page, this.totalPages);
      const start = (safePage - 1) * this.pageSize;
      return this.visibleFiles.slice(start, start + this.pageSize);
    }
  },
  watch: {
    visibleFiles() {
      this.page = 1;
    },
    totalPages(nextTotal) {
      if (this.page > nextTotal) {
        this.page = Math.max(1, nextTotal);
      }
    }
  },
  async mounted() {
    await this.loadMedia();
    await nextTick();
    this.observeGridWidth();
  },
  beforeUnmount() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  },
  methods: {
    observeGridWidth() {
      const grid = this.$refs.gridRef as HTMLElement | undefined;
      const ResizeObserverCtor = (window as any).ResizeObserver;
      if (!grid || typeof ResizeObserverCtor === "undefined") {
        this.gridWidth = grid?.clientWidth || 0;
        return;
      }

      this.resizeObserver?.disconnect();
      const observer = new ResizeObserverCtor((entries: Array<{ contentRect: { width: number } }>) => {
        const entry = entries[0];
        if (entry) {
          this.gridWidth = entry.contentRect.width;
        }
      });
      this.resizeObserver = observer;
      observer.observe(grid);
      this.gridWidth = grid.clientWidth;
    },
    async loadMedia() {
      this.loading = true;
      try {
        const response = await listContentMedia({ limit: 50, offset: 0 });
        const data = response.data || {};
        this.files = data.files || [];
        this.usedBytes = data.usedBytes || 0;
        this.limitBytes = data.limitBytes || 0;
      } catch {
        this.files = [];
      } finally {
        this.loading = false;
        await nextTick();
        this.observeGridWidth();
      }
    },
    getPublicUrlForId(id: number) {
      const file = this.files.find((entry) => entry.id === id);
      return file?.publicUrl || "";
    },
    getFileForId(id: number) {
      return this.files.find((entry) => entry.id === id) || null;
    },
    formatBytes(bytes: number) {
      if (!bytes) return "0 B";
      const units = ["B", "KB", "MB", "GB"];
      let value = bytes;
      let unit = 0;
      while (value >= 1024 && unit < units.length - 1) {
        value /= 1024;
        unit += 1;
      }
      return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
    },
    isImage(file: any) {
      const mime = String(file.mimeType || "");
      return mime.startsWith("image/") && file.publicUrl;
    },
    thumbUrl(file: any) {
      if (this.isImage(file)) {
        return file.publicUrl;
      }
      return file.thumbnailUrl || null;
    },
    statusClass(status: string) {
      if (status === "ready") return "badge-light-success";
      if (status === "pending") return "badge-light-warning";
      if (status === "failed") return "badge-light-danger";
      return "badge-light";
    },
    canAttach(file: any) {
      return file.status === "ready" && !!file.publicUrl;
    },
    toggleAttach(file: any) {
      if (this.readOnly || !this.canAttach(file)) return;
      const ids = [...this.selectedIds];
      const index = ids.indexOf(file.id);
      if (index >= 0) {
        ids.splice(index, 1);
      } else {
        if (this.singleVideo && !this.isImage(file)) {
          const nextIds = ids.filter((id) => {
            const existing = this.files.find((entry) => entry.id === id);
            return existing && this.isImage(existing);
          });
          nextIds.push(file.id);
          this.$emit("update:selectedIds", nextIds);
          return;
        }
        if (this.maxSelection > 0 && ids.length >= this.maxSelection) {
          return;
        }
        ids.push(file.id);
      }
      this.$emit("update:selectedIds", ids);
    },
    async confirmDelete(file: any) {
      const result = await Swal.fire({
        title: "Delete file?",
        text: `Remove "${file.originalFilename}" from storage?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Delete",
        cancelButtonText: "Cancel",
        confirmButtonClass: "btn btn-danger",
        cancelButtonClass: "btn btn-light"
      });
      if (!result.isConfirmed) return;

      try {
        await deleteContentMedia(file.id);
        const ids = this.selectedIds.filter((id) => id !== file.id);
        this.$emit("update:selectedIds", ids);
        await this.loadMedia();
        this.$emit("refresh");
        Swal.fire({
          title: "",
          text: "File deleted.",
          icon: "success",
          confirmButtonClass: "btn btn-secondary"
        });
      } catch (e) {
        Swal.fire({
          title: "",
          text: e?.response?.data?.message || "Unable to delete file.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
      }
    }
  }
});
